// ============================================================
// TERROR — useEquippedCompanion HOOK  (THE COMPANION FAILSAFE)
// ------------------------------------------------------------
// Subscribes a component to the player's equipped companion +
// radiant sheen, and returns a normalized, SELF-VALIDATED object
// on every change:
//
//   { companionId, radiantTier }
//
// This is the companion twin of useEquippedCosmetics. It exists so
// that ANY zone — Plaza, Workshop, FactionHall, Dungeon, Codex,
// Unstable Pierre, Endless Depths, and every future room — can show
// the player's companion (and its radiant foil) with ONE line of
// setup, and can NEVER fall out of sync, because every room reads
// from this single source.
//
// ── WHY THIS IS A FAILSAFE ──────────────────────────────────
//  1. Single source of truth. Rooms never hardcode a companionId.
//     They call this hook. If the equipped companion changes in the
//     Plaza, every other room updates the next time it mounts/reads.
//  2. Self-validating. If the stored companionId no longer exists in
//     COMPANIONS (renamed, removed, corrupted), the hook returns the
//     starter/default instead of a broken id — a room can never crash
//     or render a missing sprite because of stale profile data.
//  3. Null-safe. Call it with uid=null (logged out, loading) and it
//     returns a safe default with NO Firebase subscription.
//  4. Self-cleaning. Tears down its listener on unmount / uid change.
//
// ── USAGE (one line in any room) ────────────────────────────
//   const companion = useEquippedCompanion(uid);
//   ...
//   <PlayerEntity
//     {...playerProps}
//     companionId={companion.companionId}
//     radiantTier={companion.radiantTier}
//     showCompanion
//   />
//
// That's it. The companion now follows the player in that room,
// wearing whatever radiant sheen the player equipped in the Plaza.
// ============================================================

import { useState, useEffect } from "react";
import { listenToProfile } from "./progression";
import { COMPANIONS, getStarterCompanionId } from "./companions";
import { equippedGraceOf, renderableGrace } from "./kingsGrace";

// Safe fallback when nothing is equipped / uid is null.
// companionId stays null so rooms simply render no companion rather
// than guessing — but resolveCompanionId() below will upgrade a
// logged-in player with a starter to their starter companion.
const EMPTY = { companionId: null, radiantTier: null, graceSource: null };

// ── Self-validation ─────────────────────────────────────────
// Given the raw profile, decide which companionId to actually
// render. Priority:
//   1. equippedCompanion, IF it exists in COMPANIONS
//   2. the player's starter companion (derived from starterDeckId)
//   3. null (render nothing)
function resolveCompanionId(profile) {
  if (!profile) return null;

  const equipped = profile.equippedCompanion;
  if (equipped && COMPANIONS[equipped]) {
    return equipped;            // valid, use it
  }

  // equipped is missing OR points at an id we no longer have.
  // Fall back to the starter so the player is never companion-less
  // in a room just because of stale/renamed data.
  const starter = getStarterCompanionId
    ? getStarterCompanionId(profile.starterDeckId)
    : null;
  if (starter && COMPANIONS[starter]) {
    return starter;
  }

  return null;
}

// A worn Grace only "counts" if the profile actually holds it for the
// companion we resolved (kingsGrace.equippedGraceOf). Switching
// companions, a hand-edited field, a retired foil — all read as nothing.
// `radiantTier` keeps its name for every caller; it carries a Grace id.
// (Before King's Grace this trusted the raw field; see git history.)
function resolveRadiantTier(profile, companionId) {
  if (!profile || !companionId) return null;
  const worn = equippedGraceOf(profile);
  if (!worn || worn.companionId !== companionId) return null;
  return renderableGrace(worn.graceId);
}

export function useEquippedCompanion(uid) {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (!uid) {
      setState(EMPTY);
      return;
    }

    const unsub = listenToProfile(uid, (profile) => {
      const companionId = resolveCompanionId(profile);
      const radiantTier = resolveRadiantTier(profile, companionId);
      const graceSource = radiantTier ? (equippedGraceOf(profile)?.source || null) : null;
      setState({ companionId, radiantTier, graceSource });
    });

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [uid]);

  return state;
}

export default useEquippedCompanion;
